import http from '@/http';
import type { TenantConfig, TokenUsageSummary } from '@/interfaces/admin';

/**
 * Fetches the knowledge base configuration for a specific tenant.
 * @param tenantId - The ID of the tenant.
 * @returns A promise that resolves to the TenantConfig.
 */
export async function getTenantConfig(tenantId: number): Promise<TenantConfig> {
  return await http.get(`/knowledge-base/tenants/${tenantId}/configs`)
    .then(res => res.data);
}

/**
 * Fetches the token usage summary for a specific tenant, for a given year and month.
 * Defaults to the current year and month if not specified.
 * @param tenantId The ID of the tenant.
 * @param year Optional. The year for which to fetch usage data.
 * @param month Optional. The month (1-12) for which to fetch usage data.
 * @returns A promise that resolves to the TokenUsageSummary.
 */
export async function getTenantUsage(
  tenantId: number,
  year?: number,
  month?: number
): Promise<TokenUsageSummary> {
  let yearToUse = year;
  let monthToUse = month;

  if (yearToUse === undefined || monthToUse === undefined) {
    const now = new Date();
    yearToUse = now.getFullYear();
    monthToUse = now.getMonth() + 1; // getMonth() is 0-indexed
  }

  return await http.get(`/knowledge-base/tenants/${tenantId}/usage?year=${yearToUse}&month=${monthToUse}`)
    .then(res => res.data);
}

/**
 * Updates the knowledge base configuration for a specific tenant.
 * @param tenantId The ID of the tenant.
 * @param configData The configuration data to update.
 * @returns A promise that resolves to the updated TenantConfig.
 */
export async function updateTenantConfig(
  tenantId: number,
  configData: { enabled: boolean; maxTokensPerMonthOverride: number | null }
): Promise<TenantConfig> {
  return await http.put(`/knowledge-base/tenants/${tenantId}/configs`, configData)
    .then(res => res.data);
}
